import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, Home, Leaf, Clock, Cpu, Briefcase } from 'lucide-react';
import Modal from '../shared/Modal';
import ContactForm from '../forms/ContactForm';

const ModuulizeCaseStudy: React.FC = () => {
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);

  const handleBack = () => {
    navigate('/case-studies');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center text-[#00FFFF] hover:text-[#00FFFF]/80 transition-colors mb-8 mt-16 px-4 py-2 bg-black/20 rounded-lg border border-[#00FFFF]/20 hover:border-[#00FFFF]/40"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Case Studies
        </button>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#00FFFF] to-purple-500 text-transparent bg-clip-text">
              Moduulize
            </span>
          </h1>
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="px-3 py-1 bg-[#00FFFF]/10 text-[#00FFFF] rounded-full text-sm">
              Modular Construction
            </span>
            <span className="px-3 py-1 bg-purple-500/10 text-purple-400 rounded-full text-sm">
              Digital Transformation
            </span>
            <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-sm">
              Sustainable Solutions
            </span>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl">
            Revolutionizing construction with flexible, scalable, and profitable modular solutions for cannabis grow facilities, office spaces, and modular homes - all powered by SELF Labs' digital transformation expertise.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column */}
          <div className="space-y-12">
            {/* Partnership Section */}
            <motion.section
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">The Partnership</h2>
              <div className="space-y-4">
                <p className="text-gray-300">
                  SELF Labs Inc., led by Milan Cheeks, partnered with Moduulize to develop a comprehensive digital ecosystem that would streamline their modular construction process and enhance customer experience.
                </p>
                <p className="text-gray-300">
                  This collaboration brought together SELF Labs' technological expertise with Moduulize's innovative approach to modular construction, creating a powerful synergy that has transformed the industry.
                </p>
              </div>
            </motion.section>

            {/* Technology Section */}
            <motion.section
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">Innovative Technology</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#00FFFF]/10 rounded-lg">
                    <Cpu className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Smart Modular Systems</h3>
                    <p className="text-gray-300">
                      Implementation of IoT sensors and smart systems for real-time monitoring of modular environments, ensuring optimal conditions for cannabis cultivation and workspace efficiency.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-purple-500/10 rounded-lg">
                    <Briefcase className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Digital Design Platform</h3>
                    <p className="text-gray-300">
                      Creation of an intuitive digital platform that allows customers to customize and visualize their modular solutions before production, streamlining the design process.
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>
          </div>

          {/* Right Column */}
          <div className="space-y-12">
            {/* Process Section */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">Solution Areas</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <Leaf className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Cannabis Grow Facilities</h3>
                    <p className="text-gray-300">
                      Smart modular systems that enhance crop quality and yield while ensuring compliance with local regulations.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <Building className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Office & Workspace Solutions</h3>
                    <p className="text-gray-300">
                      Flexible, rapidly deployable spaces that can be reconfigured as business needs evolve.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-yellow-500/10 rounded-lg">
                    <Home className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Modular Homes</h3>
                    <p className="text-gray-300">
                      Sustainable, efficient living spaces that can be installed in a fraction of the time of traditional construction.
                    </p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Impact Section */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-[#121214] p-8 rounded-2xl border border-white/10"
            >
              <h2 className="text-2xl font-bold mb-6">Impact & Results</h2>
              <div className="space-y-4">
                <p className="text-gray-300">
                  Through this partnership, Moduulize has achieved a 40% reduction in design-to-delivery time, now completing projects in just 12 weeks. Their digital platform has increased customer engagement by 75% and has expanded their reach to global markets.
                </p>
                <div className="flex items-start space-x-4 mt-6">
                  <div className="p-3 bg-[#00FFFF]/10 rounded-lg">
                    <Clock className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">12-Week Turnaround</h3>
                    <p className="text-gray-300">
                      From concept to completion in just 12 weeks, delivering maximum efficiency and faster ROI for clients.
                    </p>
                  </div>
                </div>
                <a
                  href="https://moduulize.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-[#00FFFF] text-black font-medium rounded-lg hover:bg-[#00FFFF]/90 transition-colors mt-4"
                >
                  Visit Moduulize
                </a>
              </div>
            </motion.section>
          </div>
        </div>

        {/* Contact Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <button
            onClick={() => setShowContactModal(true)}
            className="px-8 py-4 bg-gradient-to-r from-[#00FFFF] to-purple-500 text-black font-medium rounded-lg hover:opacity-90 transition-opacity"
          >
            Contact Us About Modular Solutions
          </button>
        </motion.div>
      </div>

      {/* Contact Modal */}
      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title="Contact Us About Modular Solutions"
      >
        <ContactForm />
      </Modal>
    </div>
  );
};

export default ModuulizeCaseStudy; 